import type { ReactNode } from 'react';
import { Box, CircularProgress, Stack, Typography, type SxProps, type Theme } from '@mui/material';

// The history column on the right of each generator page: a sticky bordered
// panel with a small uppercase header and a scrolling list of rows, each a
// 72×48 thumbnail plus a few lines of text. RecentGenerationsStrip (the
// generator pages) and the video agent's chat list both draw with these.

export const HISTORY_PANEL_WIDTH = 220;

export function HistoryPanel({
  title,
  action,
  width = HISTORY_PANEL_WIDTH,
  sticky = true,
  sx,
  children,
}: {
  title: ReactNode;
  action?: ReactNode;
  width?: number | string;
  // Off when the panel is not in a page column (e.g. inside a drawer).
  sticky?: boolean;
  sx?: SxProps<Theme>;
  children: ReactNode;
}) {
  return (
    <Box
      sx={[
        {
          width,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        },
        sticky && { position: 'sticky', top: 16, maxHeight: 'calc(100vh - 48px)' },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 1.5, py: action ? 0.5 : 1, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}
      >
        <Typography
          variant="caption"
          fontWeight={600}
          sx={{ flex: 1, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
          {title}
        </Typography>
        {action}
      </Stack>
      <Box sx={{ flex: 1, overflowY: 'auto' }}>{children}</Box>
    </Box>
  );
}

// Spinner or empty-state text in place of the list.
export function HistoryPanelMessage({ loading, children }: { loading?: boolean; children?: ReactNode }) {
  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  return (
    <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', textAlign: 'center', py: 4, px: 1 }}>
      {children}
    </Typography>
  );
}

export function HistoryPanelRow({
  selected,
  onClick,
  alignItems = 'center',
  children,
}: {
  selected: boolean;
  onClick?: () => void;
  alignItems?: 'center' | 'flex-start';
  children: ReactNode;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems,
        gap: 1,
        px: 1,
        py: 0.75,
        borderRadius: 1,
        cursor: onClick ? 'pointer' : 'default',
        bgcolor: selected ? 'action.selected' : 'transparent',
        outline: selected ? '1px solid' : 'none',
        outlineColor: 'primary.main',
        '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover' },
        transition: 'background-color 0.1s',
      }}
      onClick={onClick}
    >
      {children}
    </Box>
  );
}

export function HistoryThumb({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        width: 72,
        height: 48,
        flexShrink: 0,
        borderRadius: 0.75,
        overflow: 'hidden',
        bgcolor: 'black',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {children}
    </Box>
  );
}
